import * as React from "react";
import { RecentlyViewedHotelsCardProps } from "../typings";
import { PriceContent } from "./PriceContent";
import { LocalizedText } from "bernie-l10n";
import { useLocalization } from "@shared-ui/localization-context";
import {
  UitkCard,
  UitkCardImageLayout,
  UitkCardMedia,
  UitkCardContent,
  UitkCardTextSegment,
  UitkCardLink
} from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { LazyLoadedImage } from "src/components/utility/LazyLoadedImage/LazyLoadedImage";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import TruncatedText from "shiitake";

enum VariationType {
  PRICE_INCREASE = "increase",
  PRICE_DECREASE = "decrease",
  AVAILABLE = "available",
  SOLD_OUT = "sold_out"
}

export const RecentlyViewedHotelCard = (props: RecentlyViewedHotelsCardProps) => {
  const { formatText, formatDateString } = useLocalization();
  const dateFormat = "MMMd";
  const checkInDateFormatted = formatDateString(props.startDate?.raw, { skeleton: dateFormat });
  const checkOutDateFormatted = formatDateString(props.endDate?.raw, { skeleton: dateFormat });

  const getPriceVariation = () => {
    let priceMessage = formatText("hotels.price.perNight");
    const priceToShow = props.newPriceFormatted || props.originalSearchPrice?.formatted;
    if (props.priceChange) {
      switch (props.priceChange) {
        case VariationType.SOLD_OUT: {
          return { isSoldOut: true };
        }
        case VariationType.PRICE_INCREASE: {
          priceMessage = formatText("recentlyViewedHotels.increasePrice", props.originalSearchPrice?.formatted);
          break;
        }
        case VariationType.PRICE_DECREASE: {
          priceMessage = formatText("recentlyViewedHotels.decreasePrice", props.originalSearchPrice?.formatted);
          break;
        }
      }
    }

    return { isSoldOut: false, priceToShow, priceMessage };
  };

  const properties = getPriceVariation();

  return (
    <UitkCard
      className="uitk-grid"
      data-testid={`recently-viewed-card-${props.cardIndex}`}
      imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}
      withBorder={false}
    >
      <UitkCardMedia pxWidth={80}>
        <UitkFigure className="all-image-fit-cover" ratio={UitkFigureAspectRatioType.R1_1}>
          <LazyLoadedImage src={props.productInfo?.gallery.featured?.url} alt={props.productInfo?.summary.name} />
        </UitkFigure>
      </UitkCardMedia>

      <UitkCardContent className="uitk-cell uitk-grid">
        <UitkCardTextSegment className="uitk-flex uitk-flex-column uitk-flex-justify-content-space-between">
          <div className="uitk-cell">
            <TruncatedText
              lines={1}
              renderFullOnServer={true}
              overflowNode={"\u2026"}
              className="hotelTitle uitk-type-heading-500"
              tagName="h3"
            >
              {props.productInfo?.summary.name}
            </TruncatedText>
          </div>
          <div>
            {props.isPriceEnabled && (
              <PriceContent
                isSoldOut={properties.isSoldOut}
                priceToShow={properties.priceToShow}
                priceMessage={properties.priceMessage}
              />
            )}
            <div className={`uitk-type-${props.isPriceEnabled ? "100" : "300"}`}>
              {formatText("hotels.whenBooking", checkInDateFormatted, checkOutDateFormatted)}
            </div>
          </div>
        </UitkCardTextSegment>
        <UitkCardLink>
          <TrackedLink moduleName="recently-viewed-hotels" href={props.hisUrl} rfrr={`card-${props.cardIndex}`}>
            <LocalizedText message="hotels.hotel.open.page" data={[props.productInfo?.summary.name || ""]} />
          </TrackedLink>
        </UitkCardLink>
      </UitkCardContent>
    </UitkCard>
  );
};
